import { ROLE_SLUGS, PERMISSION_SLUGS } from '../../../utils/auth/permissionConstants';

const buildHomeItem = ({ hasActiveWallet, hasVaultAdmin, isExecAdmin, hasSoloUser }) => {
    if (hasActiveWallet) {
        return {
            title: 'Home',
            iconStyle: <i className="material-icons">home</i>,
            to: '/my-balance-dashboard',
            requiresUserWallet: true,
        };
    }
    // Solo-user partners without an active wallet still get home routed
    // to the wallet dashboard rather than the vault dashboard, because
    // the vault dashboard is hidden from them entirely.
    if (hasVaultAdmin && !hasSoloUser) {
        return {
            title: 'Home',
            iconStyle: <i className="material-icons">home</i>,
            to: '/vault-dashboard',
            requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
            requiresPartnerVault: true,
        };
    }
    if (isExecAdmin) {
        return {
            title: 'Home',
            iconStyle: <i className="material-icons">home</i>,
            to: '/health',
            requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
        };
    }
    return null;
};

export const getMenuList = ({ onLogout, onChangePin, onForgotPin, hasActiveWallet, hasVaultAdmin, isExecAdmin, hasSoloUser } = {}) => {
    const homeItem = buildHomeItem({ hasActiveWallet, hasVaultAdmin, isExecAdmin, hasSoloUser });
    // Solo-user partners: the Admin > Top Up shortcut and Admin > Dashboard
    // entry both point at /vault-dashboard, which is hidden for them.
    // Their top-up flow lives on the wallet dashboard tab instead. During
    // the load window we default to team (entries visible), so team users
    // never see the entries pop in a beat after the menu renders. A solo
    // user briefly sees them and then they disappear when the check
    // resolves; that is the smaller of the two visible glitches and none
    // of the entries would harm a super admin to briefly see.
    const vaultDashboardEntries = hasSoloUser
        ? []
        : [
            {
                title: 'Top Up',
                to: '/vault-dashboard',
                requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
                requiresPartnerVault: true,
                isShortcut: true,
            },
            ...(hasVaultAdmin && !hasActiveWallet ? [] : [{
                title: 'Dashboard',
                to: '/vault-dashboard',
                requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DASHBOARD,
                requiresPartnerVault: true,
            }]),
        ];
    // Solo super admins do not need an Admin submenu. Insights duplicates
    // Mili Insights (identical data when there is one user). Ledger,
    // Approvals, and Receipts either duplicate personal surfaces or have
    // no second party to act on. Organization is the only remaining item
    // and lives as a top level entry instead. Team users keep the whole
    // Admin group unchanged.
    // Default to the team shape (Admin submenu) during the load window
    // so team users never see the menu shape change on cold load. A
    // solo user briefly sees the team Admin submenu before it flips to
    // Top Up + Organization when the check resolves; the entries the
    // team submenu carries would not harm a super admin to see for a
    // moment. Session-cached after the first load.
    const adminGroupForSolo = hasSoloUser ? [
        {
            title: 'Top Up',
            iconStyle: <i className="material-icons">add_card</i>,
            to: '/my-balance-dashboard?action=top-up',
            requiresUserWallet: true,
            isShortcut: true,
        },
        {
            title: 'Organization',
            iconStyle: <i className="material-icons">business</i>,
            to: '/admin-details',
            requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
            requiresPartner: true,
        },
    ] : [{
        title: 'Admin',
        iconStyle: <i className="material-icons">admin_panel_settings</i>,
        classsChange: 'admin-menu',
        content: [
            ...vaultDashboardEntries,
            {
                title: 'Organization',
                to: '/admin-details',
                requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
                requiresPartner: true,
            },
            {
                title: 'Insights',
                to: '/expense-tag-stats',
                requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_DETAILS,
            },
            {
                title: 'Ledger',
                to: '/ledger',
                requiredPermissionSlug: PERMISSION_SLUGS.VIEW_PARTNER_STATEMENTS,
            },
            {
                title: 'Approvals',
                to: '/approvals',
                requiredPermissionSlug: PERMISSION_SLUGS.CAN_REVIEW_APPROVALS,
            },
            {
                title: 'Receipts',
                to: '/manage-receipts',
                requiredPermissionSlug: PERMISSION_SLUGS.MANAGE_RECEIPTS,
            },
        ],
    }];

    return [
    ...(homeItem ? [homeItem] : []),
    ...adminGroupForSolo,
    {
        title: 'Pay',
        iconStyle: <i className="material-icons">payment</i>,
        to: '/my-balance-dashboard',
        requiresUserWallet: true,
        isShortcut: true,
    },
    {
        title: 'Partners',
        iconStyle: <i className="material-icons">manage_accounts</i>,
        to: '/manage-partners',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Capital Injections',
        iconStyle: <i className="material-icons">account_balance</i>,
        to: '/capital-injections',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Reversals',
        iconStyle: <i className="material-icons">undo</i>,
        to: '/reversals',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Fund Transfers',
        iconStyle: <i className="material-icons">swap_horiz</i>,
        to: '/fund-transfers',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Fee Rates',
        iconStyle: <i className="material-icons">percent</i>,
        to: '/fee-rates',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Chart of Accounts',
        iconStyle: <i className="material-icons">account_tree</i>,
        to: '/chart-of-accounts',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Executive Admins',
        iconStyle: <i className="bi bi-award-fill"></i>,
        to: '/manage-exec-admins',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Roles & Permissions',
        iconStyle: <i className="material-icons">lock</i>,
        to: '/roles-and-permissions',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Reconciliation',
        iconStyle: <i className="material-icons">build_circle</i>,
        to: '/reconciliation',
        requiredRoleSlug: ROLE_SLUGS.EXEC_ADMIN,
    },
    {
        title: 'Mili Insights',
        iconStyle: <i className="material-icons">donut_small</i>,
        to: '/my-expense-tag-stats',
        requiresUserWallet: true,
    },
    {
        title: 'Mili History',
        iconStyle: <i className="material-icons">history</i>,
        to: '/my-activity',
        requiresUserWallet: true,
    },
    {
        title: 'Mili Receipts',
        iconStyle: <i className="bi bi-receipt"></i>,
        to: '/my-receipts',
        requiresUserWallet: true,
    },
    // Mili Requests hidden for solo super admins (they top up their own
    // wallet directly). Default to visible during the load window so
    // team users see it immediately; a solo user briefly sees it and
    // then it disappears when the check resolves. The entry links to
    // the user's own top-up request page, which a super admin could
    // legitimately open to a slightly different-shaped page, so brief
    // exposure is not a footgun.
    ...(hasSoloUser ? [] : [{
        title: 'Mili Requests',
        iconStyle: <i className="bi bi-clipboard"></i>,
        to: '/my-requests',
        requiresUserWallet: true,
    }]),
    {
        title: 'Help & FAQ',
        iconStyle: <i className="material-icons">help_outline</i>,
        to: '/faq',
        dataTour: 'sidebar-faq-link',
    },
    {
        title: 'My Security',
        iconStyle: <i className="material-icons">shield</i>,
        classsChange: 'security-menu',
        dataTour: 'sidebar-security-menu',
        content: [
            {
                title: 'Sign-in activity',
                to: '/sign-in-activity',
                dataTour: 'sidebar-security-sign-in-activity',
            },
            {
                title: 'Change PIN',
                onClick: onChangePin,
                isAction: true,
                requiresTransactionPinCapability: true,
                dataTour: 'sidebar-security-change-pin',
            },
            {
                title: 'Forgot PIN',
                onClick: onForgotPin,
                isAction: true,
                requiresTransactionPinCapability: true,
                dataTour: 'sidebar-security-forgot-pin',
            },
            {
                title: 'Logout',
                onClick: onLogout,
                isAction: true,
                dataTour: 'sidebar-security-logout',
            },
        ],
    },
];
};
